import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Shield, Heart, FolderHeart, Check, Globe2 } from "lucide-react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { localized, LANGS } from "@/i18n";
import { CATEGORIES, NEIGHBORHOODS, priceSymbol } from "@/constants";
import { useToast } from "@/hooks/use-toast";

export default function Profile() {
  const navigate = useNavigate();
  const { t, lang, setLang, prefs, setPrefs, favorites, lists, user, logout } = useApp();
  const { toast } = useToast();
  const [draft, setDraft] = useState(prefs);
  const [logoutError, setLogoutError] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = async () => {
    setLoggingOut(true); setLogoutError(false);
    try { const result = await logout(); if (result.unsaved) toast({ title: t("logout_unsaved"), variant: "destructive" }); } catch { setLogoutError(true); } finally { setLoggingOut(false); }
  };

  const toggleArr = (key, val) => {
    const arr = draft[key] || [];
    setDraft({ ...draft, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const save = () => { setPrefs(draft); toast({ title: t("saved") }); };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-extrabold text-ink">{t("profile")}</h1>

      {/* Account */}
      <section className="rounded-2xl border border-border bg-white p-4">
        <p className="mb-3 text-xs font-bold uppercase tracking-wide text-ink-muted">{t("account")}</p>
        {user ? (
          <div className="flex items-center gap-3">
            {user.picture ? <img src={user.picture} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="grid h-12 w-12 place-items-center rounded-full bg-terracotta text-white font-bold">{user.name?.[0] || "U"}</div>}
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{user.name}</p>
              <p className="truncate text-xs text-ink-muted">{user.email}</p>
            </div>
            <button data-testid="logout-btn" title={t("logout")} disabled={loggingOut} onClick={handleLogout} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-red-50 hover:text-red-500 disabled:opacity-50"><LogOut className="h-5 w-5" /></button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-ink-muted">{t("login_hint")}</p>
            <button data-testid="login-google-btn" onClick={loginGoogle} className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-white py-3 text-sm font-bold text-ink shadow-sm hover:bg-sand">
              <Globe2 className="h-4.5 w-4.5 text-sea" /> {t("login_google")}
            </button>
          </div>
        )}
        {user?.is_admin && (
          <button data-testid="admin-link" onClick={() => navigate("/admin")} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-sea py-3 text-sm font-bold text-white hover:bg-sea-hover">
            <Shield className="h-4.5 w-4.5" /> {t("admin")}
          </button>
        )}
        {logoutError && <p data-testid="logout-error" role="alert" className="mt-3 text-sm text-red-700">{t("sync_error")} · {t("retry")}</p>}
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl bg-terracotta-light p-4"><Heart className="h-5 w-5 text-terracotta" /><p className="mt-2 font-heading text-2xl font-extrabold text-ink">{favorites.length}</p><p className="text-xs text-ink-muted">{t("my_favorites")}</p></div>
        <div className="rounded-2xl bg-sea-light p-4"><FolderHeart className="h-5 w-5 text-sea" /><p className="mt-2 font-heading text-2xl font-extrabold text-ink">{lists.length}</p><p className="text-xs text-ink-muted">{t("my_lists")}</p></div>
      </div>

      {/* Language */}
      <section>
        <p className="mb-2 text-sm font-semibold text-ink">{t("lang_label")}</p>
        <div className="grid grid-cols-3 gap-2">
          {LANGS.map((l) => (
            <button key={l.code} data-testid={`profile-lang-${l.code}`} onClick={() => setLang(l.code)} className={`rounded-xl border py-3 text-sm font-bold ${lang === l.code ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-ink"}`}>{l.name}</button>
          ))}
        </div>
      </section>

      {/* Preferred budget */}
      <section>
        <p className="mb-2 text-sm font-semibold text-ink">{t("pref_budget")}</p>
        <div className="flex gap-2">
          {[0, 1, 2, 3].map((p) => (
            <button key={p} data-testid={`pref-budget-${p}`} onClick={() => setDraft({ ...draft, budget: p })} className={`flex-1 rounded-xl border py-2.5 text-sm font-bold ${(draft.budget || 0) === p ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-ink"}`}>{p === 0 ? t("any") : priceSymbol(p)}</button>
          ))}
        </div>
      </section>

      {/* Preferred activities */}
      <section>
        <p className="mb-2 text-sm font-semibold text-ink">{t("pref_activities")}</p>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const active = (draft.activities || []).includes(c.id);
            const Icon = Icons[c.icon] || Icons.MapPin;
            return (
              <button key={c.id} data-testid={`pref-activity-${c.id}`} onClick={() => toggleArr("activities", c.id)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold ${active ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border bg-white text-ink"}`}>
                <Icon className="h-4 w-4" /> {localized(c.label, lang)} {active && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      </section>

      {/* Preferred neighborhoods */}
      <section>
        <p className="mb-2 text-sm font-semibold text-ink">{t("pref_neighborhoods")}</p>
        <div className="flex flex-wrap gap-2">
          {NEIGHBORHOODS.map((n) => {
            const active = (draft.neighborhoods || []).includes(n);
            return (
              <button key={n} data-testid={`pref-hood-${n}`} onClick={() => toggleArr("neighborhoods", n)} className={`rounded-full border px-3.5 py-2 text-sm font-semibold ${active ? "border-sea bg-sea text-white" : "border-border bg-white text-ink"}`}>{n}</button>
            );
          })}
        </div>
      </section>

      <button data-testid="save-prefs-btn" onClick={save} className="w-full rounded-2xl bg-terracotta py-3.5 text-sm font-bold text-white hover:bg-terracotta-hover">{t("save")}</button>
    </div>
  );
}
